import { useEffect, useRef, useState, type FormEvent } from "react";
import { Alert, Button, InputField, OptionCard, RadioGroup } from "@sortera/react-components";
import { ArrowTopRightOnSquareIcon, CheckIcon, PlusIcon } from "@sortera/react-icons";
import type { CreateProjectDto, ProjectCreationDto } from "../../types/index.ts";
import { createProject, fetchProjectCreation } from "../../integrations/api.ts";
import { ApiError } from "../../integrations/http.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import styles from "./create-project.module.css";

type Field = keyof CreateProjectDto;

/** How often a running Factory run is looked at. It takes about a minute; this is plenty. */
const POLL_MS = 3_000;

/**
 * A new project, made here: the Sortera Factory runs as the person, and the project opens when it
 * is done. The checks are the Factory's own, made by the server before anything is queued.
 */
export function CreateProject({ defaultEmail, onCreated }: { defaultEmail: string; onCreated: (repo: string) => void }) {
  const [name, setName] = useState("");
  const [profile, setProfile] = useState<CreateProjectDto["profile"]>("poc");
  const [budgetEmail, setBudgetEmail] = useState(defaultEmail);
  const [problem, setProblem] = useState<{ field: Field | null; message: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [creation, setCreation] = useState<ProjectCreationDto | null>(null);
  /** Opening happens once, however often this renders after the run is done. */
  const opened = useRef(false);

  // The address arrives after the first render; fill it in unless the person already typed one.
  useEffect(() => setBudgetEmail((current) => current || defaultEmail), [defaultEmail]);

  // Follow the run until it is done, then open the project.
  useEffect(() => {
    if (!creation || creation.state === "failed" || creation.state === "canceled") return;
    if (creation.state === "succeeded") {
      if (!opened.current) onCreated(creation.repo);
      opened.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchProjectCreation(creation.id)
        .then(setCreation)
        .catch(() => setCreation((c) => c && { ...c })); // a hiccup: try again on the next tick
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [creation, onCreated]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSending(true);
    setProblem(null);
    try {
      setCreation(await createProject({ name, profile, budgetEmail }));
    } catch (e) {
      setProblem(
        e instanceof ApiError
          ? { field: (e.field as Field | null) ?? null, message: e.message }
          : { field: null, message: "Projektet kunde inte skapas. Försök igen." },
      );
    } finally {
      setSending(false);
    }
  };

  if (creation) return <Progress creation={creation} onRetry={() => setCreation(null)} />;

  const fieldError = (field: Field) => (problem?.field === field ? problem.message : undefined);

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)} noValidate>
      <p className={styles.intro}>
        Ge projektet ett namn, så skapar Sortera Factory repo, pipeline och testmiljö åt dig. Det tar ungefär en minut,
        sedan bygger du vidare här.
      </p>

      <InputField
        label="Projektnamn"
        placeholder="till exempel containerkoll"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={Boolean(fieldError("name"))}
        supportingText={fieldError("name") ?? `${name.length}/18 tecken. Bokstäver, siffror och bindestreck.`}
        autoComplete="off"
        spellCheck={false}
        required
      />

      <RadioGroup label="Vad är det?" value={profile} onValueChange={(v) => setProfile(v as CreateProjectDto["profile"])}>
        <div className={styles.choices}>
          <OptionCard
            type="radio"
            value="poc"
            label={
              <span className={styles.choice}>
                <strong>En idé att pröva</strong>
                <span>Påhittad data, kostar nästan ingenting. Välj den här om du tvekar.</span>
              </span>
            }
          />
          <OptionCard
            type="radio"
            value="mvp"
            label={
              <span className={styles.choice}>
                <strong>Något riktiga användare ska använda</strong>
                <span>Idén är redan bevisad. Kostar pengar från första dagen.</span>
              </span>
            }
          />
        </div>
      </RadioGroup>

      <InputField
        label="Vem får budgetvarningar?"
        type="email"
        value={budgetEmail}
        onChange={(e) => setBudgetEmail(e.target.value)}
        error={Boolean(fieldError("budgetEmail"))}
        supportingText={fieldError("budgetEmail") ?? "Ett mejl om testmiljön närmar sig sin månadsbudget."}
      />

      {problem && !problem.field && (
        <Alert variant="critical" title="Projektet kunde inte skapas">
          {problem.message}
        </Alert>
      )}

      <div className={styles.footer}>
        <span className={styles.hint}>Du blir ägare av projektet.</span>
        <Button type="submit" color="secondary" size="small" icon={sending ? <Spinner label="Skapar" /> : <PlusIcon />} disabled={sending}>
          Skapa projektet
        </Button>
      </div>
    </form>
  );
}

const STEPS = [
  { key: "queued", label: "I kö hos Sortera Factory", detail: "Körningen väntar på en ledig byggmaskin." },
  { key: "running", label: "Projektet byggs", detail: "Repo, pipeline och en egen testmiljö i Azure." },
  { key: "succeeded", label: "Klart", detail: "Projektet öppnas, och du kan börja beskriva vad du vill bygga." },
] as const;

function Progress({ creation, onRetry }: { creation: ProjectCreationDto; onRetry: () => void }) {
  const failed = creation.state === "failed" || creation.state === "canceled";
  const at = Math.max(0, STEPS.findIndex((s) => s.key === creation.state));

  return (
    <section className={styles.progress} aria-live="polite">
      <div className={styles.heading}>
        <p className="sc-kicker">{failed ? "Det gick inte" : "Skapar projektet"}</p>
        <h2 className={`sc-heading ${styles.name}`}>{creation.name}</h2>
      </div>

      {failed ? (
        <>
          <Alert variant="critical" title="Projektet kunde inte skapas">
            {creation.state === "canceled"
              ? "Körningen avbröts i Azure DevOps."
              : "Sortera Factory stannade innan projektet blev klart. Körningen visar var och varför."}
          </Alert>
          <div className={styles.footer}>
            {creation.webUrl && (
              <a className={styles.link} href={creation.webUrl} target="_blank" rel="noopener noreferrer">
                Se körningen <ArrowTopRightOnSquareIcon size={14} />
              </a>
            )}
            <Button color="secondary" size="small" onClick={onRetry}>
              Försök igen
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* How far along, in thirds — the Factory says which step, not how much of it. */}
          <div className={styles.bar} role="progressbar" aria-valuemin={0} aria-valuemax={3} aria-valuenow={at + 1}>
            <span style={{ width: `${((at + 1) / STEPS.length) * 100}%` }} />
          </div>
          <ol className={styles.steps}>
            {STEPS.map((step, i) => (
              <li key={step.key} data-state={i < at ? "done" : i === at ? "now" : "later"}>
                <span className={styles.mark} aria-hidden>
                  {i < at ? <CheckIcon size={16} /> : i === at ? <Spinner /> : null}
                </span>
                <span className={styles.stepText}>
                  <strong>{step.label}</strong>
                  <span>{step.detail}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className={styles.hint}>Det tar ungefär en minut. Stanna gärna här — projektet öppnas av sig självt.</p>
        </>
      )}
    </section>
  );
}
